import {useMemo} from 'react';

import {PANEL_BANK_CHARTS_NAME} from '../../../../util/panelbankTypes';
import {getFilledRegex} from '../utils/regex';
import {useQuickAddKeys} from './useQuickAddKeys';

export type MultiMetricPanelsMap = Map<
  string,
  {
    metrics: string[];
    defaultSection: string;
    isAlreadyAdded: boolean;
    isCrossSection: boolean;
  }
>;

/**
 * Given a regex pattern, returns a map of panels to add for multi-metric quick add.
 * Map key is the panel's regex pattern, and value contains matching metrics.
 */
export const useMultiMetricPanelsMap = (
  regex: string,
  isRegexValid: boolean
): {
  map: MultiMetricPanelsMap;
  numNotAdded: number;
  numAlreadyAdded: number;
  hasCrossSectionPanels: boolean;
} => {
  const {searchedKeys: matchedKeys, numPanelsByKey} = useQuickAddKeys(
    regex,
    'number'
  );

  return useMemo(() => {
    const result = {
      map: new Map() as MultiMetricPanelsMap,
      numNotAdded: 0,
      numAlreadyAdded: 0,
      hasCrossSectionPanels: false,
    };

    if (!regex || !isRegexValid) {
      return result;
    }

    matchedKeys.forEach(([key, spec]) => {
      const filledRegex = getFilledRegex(key, regex);

      if (!filledRegex) {
        return;
      }

      const panelToAdd = result.map.get(filledRegex);
      if (panelToAdd) {
        panelToAdd.metrics.push(key);
        // if panel matches metrics across multiple sections,
        // set Charts as default section
        if (
          !panelToAdd.isCrossSection &&
          panelToAdd.defaultSection !== spec.defaultSection
        ) {
          panelToAdd.isCrossSection = true;
          panelToAdd.defaultSection = PANEL_BANK_CHARTS_NAME;
        }
        // track if there are any cross-section panels to be added
        if (
          !result.hasCrossSectionPanels &&
          panelToAdd.isCrossSection &&
          !panelToAdd.isAlreadyAdded
        ) {
          result.hasCrossSectionPanels = true;
        }
      } else {
        const isAlreadyAdded =
          (numPanelsByKey.global.get(filledRegex) ?? 0) > 0;
        if (isAlreadyAdded) {
          result.numAlreadyAdded++;
        } else {
          result.numNotAdded++;
        }

        result.map.set(filledRegex, {
          metrics: [key],
          defaultSection: spec.defaultSection,
          isAlreadyAdded,
          isCrossSection: false,
        });
      }
    });

    return result;
  }, [regex, isRegexValid, matchedKeys, numPanelsByKey]);
};
